import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Paper, Typography, TextField, CircularProgress } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import {
  ApiGet,
  ApiPost,
  ApiPut,
  ApiDelete,
} from "../helpers/API/ApiData";
import { toast, ToastContainer } from "react-toastify";
import { SelectInput } from "../components/SelectInput";
import { DataTable } from "../components/DataTable";
import { usePermissions } from "../hooks/usePermissions";
import { PageHeader } from "../components/PageHeader";
import { buildServiceItemsColumns } from "./serviceItems/serviceItemsColumns";
import { useContentLanguage } from "../hooks/useContentLanguage";
import { LanguageTabs } from "../components/LanguageTabs";

export default function ServiceCategoryItems() {
  const { canCreate, canEdit, canDelete } = usePermissions("/service-category-items");
  const [lang, setLang] = useContentLanguage();
  const [ServiceCategoryItems, setServicCategoryItems] = useState<any>() as any;
  const [addDocument, setAddDocument] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllCategoryItem, setGetAllCategoryItem] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [categoryIdForEdit, setCategoryIdForEdit] = useState<any>();
  const [categoryIdForDelete, setCategoryIdForDelete] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);
  const [categoryData, setCategoryData] = useState<any>([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    getAllCategory();
  }, [page, countPerPage, search]);

  useEffect(() => {
    getNewCategoryData();
  }, []);

  const addServiceModelClose = () => {
    setAddDocument(false);
    setIsEditApi(false);
    setErrors({});
    setServicCategoryItems({});
    setSelectedCategory("");
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setServicCategoryItems({ ...ServiceCategoryItems, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errs: any = {};
    if (!ServiceCategoryItems?.itemsName?.trim()) {
      formIsValid = false;
      errs["itemsName"] = "*Please enter the English item name";
    }
    if (!ServiceCategoryItems?.itemsName_ger?.trim()) {
      formIsValid = false;
      errs["itemsName_ger"] = "*Bitte geben Sie den deutschen Artikelnamen ein";
    }
    if (!ServiceCategoryItems?.itemsPrice?.trim()) {
      formIsValid = false;
      errs["itemsPrice"] = "*Bitte geben Sie den St\u00fcckpreis ein";
    }
    if (!selectedCategory) {
      formIsValid = false;
      errs["selectedCategory"] = "*Bitte w\u00e4hlen Sie eine Kategorie aus";
    }
    setErrors(errs);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getNewCategoryData = async () => {
    await ApiGet(`service/find`)
      .then((res: any) => {
        setCategoryData(res?.data?.data);
      })
      .catch(() => {
        // silently ignore — category list will remain empty
      });
  };

  const getAllCategory = async () => {
    setLoaderForGetAll(true);
    const params = `page=${page}&limit=${countPerPage}${search ? `&letter=${search}` : ""}`;
    await ApiGet(`service/find-items?${params}`)
      .then((res: any) => {
        setGetAllCategoryItem(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const addCategoryItem = async () => {
    if (formValidation()) {
      setLoader(true);
      const DATA = {
        itemsName: ServiceCategoryItems?.itemsName,
        itemsName_ger: ServiceCategoryItems?.itemsName_ger,
        itemsPrice: ServiceCategoryItems?.itemsPrice,
        categoryId: selectedCategory,
      };
      await ApiPost(`service/create-items`, DATA)
        .then(() => {
          toast.success("Erfolgreich hinzuf\u00fcgen");
          setAddDocument(false);
          setIsEditApi(false);
          getAllCategory();
          setServicCategoryItems({});
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const EditCategoryItem = async () => {
    if (formValidation()) {
      setLoader(true);
      const DATA = {
        itemsName: ServiceCategoryItems?.itemsName,
        itemsName_ger: ServiceCategoryItems?.itemsName_ger,
        itemsPrice: ServiceCategoryItems?.itemsPrice,
        categoryId: selectedCategory,
      };
      await ApiPut(`service/update-items?id=${categoryIdForEdit}`, DATA)
        .then(() => {
          toast.success("Erfolgreich geupdated");
          getAllCategory();
          setAddDocument(false);
          setIsEditApi(false);
          setServicCategoryItems({});
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const DeleteCategoryItem = async () => {
    await ApiDelete(`service/delete-items?id=${categoryIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Erfolgreich gel\u00f6scht");
          setShow(false);
          setPage(1);
          getAllCategory();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const categoryOptions =
    categoryData?.map((category: any) => ({
      label: category.categoryName,
      value: category._id,
    })) || [];

  const columns = useMemo(() => buildServiceItemsColumns({
    canEdit,
    canDelete,
    lang,
    onEdit: (row: any) => {
      setIsEditApi(true);
      setAddDocument(true);
      setCategoryIdForEdit(row?._id);
      setSelectedCategory(row?.categoryId?._id);
      setServicCategoryItems({
        itemsName: row?.itemsName,
        itemsName_ger: row?.itemsName_ger,
        itemsPrice: row?.itemsPrice,
      });
    },
    onDelete: (row: any) => {
      setCategoryIdForDelete(row?._id);
      setShow(true);
    },
  }), [canEdit, canDelete, lang]);

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <PageHeader
          title="Unterkategorie"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setServicCategoryItems({});
            setErrors({});
            setSelectedCategory("");
            setAddDocument(true);
          } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllCategoryItem || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          L&ouml;schen best&auml;tigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            M&ouml;chten Sie dieses Kategorieelement wirklich l&ouml;schen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => DeleteCategoryItem()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {addDocument && (
        <Dialog fullScreen open={addDocument}>
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => addServiceModelClose()}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Dual-language column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Deutsch
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    English
                  </Typography>
                </Box>
              </Box>

              {/* Artikelname row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Artikelname
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="itemsName_ger"
                    name="itemsName_ger"
                    value={ServiceCategoryItems?.itemsName_ger || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["itemsName_ger"]}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="itemsName"
                    name="itemsName"
                    value={ServiceCategoryItems?.itemsName || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["itemsName"]}
                  </Typography>
                </Box>
              </Box>

              {/* St\u00fcckpreis row (shared) */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  St&uuml;ckpreis
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="itemsPrice"
                    name="itemsPrice"
                    value={ServiceCategoryItems?.itemsPrice || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["itemsPrice"]}
                  </Typography>
                </Box>
              </Box>

              {/* Kategorie row (shared) */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Kategorie w&auml;hlen
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                  <SelectInput
                    id="serviceCategoryItemId"
                    inputClassName="basic-single"
                    options={categoryOptions}
                    fieldName="category"
                    setValue={setSelectedCategory}
                    selectedValue={
                      categoryOptions.find((option: any) =>
                        option.value === selectedCategory ||
                        option.value === ServiceCategoryItems?.categoryId
                      )?.value || ""
                    }
                  />
                  <Typography variant="caption" color="error">
                    {errors["selectedCategory"]}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {loader ? (
                <Button variant="contained" disabled>
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    isEditApi ? EditCategoryItem() : addCategoryItem();
                  }}
                >
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                </Button>
              )}
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
